import type { DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Field, Form, FormElement, type FieldRenderProps, type FormRenderProps } from "@progress/kendo-react-form";
import { GridColumn, GridToolbar, type GridCellProps, type GridDataStateChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { arrowRotateCwIcon, pencilIcon, plusIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useCallback, useEffect, useState } from "react";
import { useCreateCustomerMutation, useDeleteCustomerMutation, useLazyListCustomersQuery, useUpdateCustomerMutation } from "../../api/admin/adminCustomerApi";
import AppGrid from "../../components/grid/appGrid";
import { FormInputField } from "../../components/kendo-form-controls";
import type { CustomerDto } from "../../contracts/customer-dto";
import { CustomerStatus } from "../../contracts/customer-status";

type CustomerFormValues = {
    name: string;
    status: CustomerStatus;
};

type CustomerFormWindowProps = {
    customer?: CustomerDto;
    errorMessage: string | null;
    isSaving: boolean;
    onClose: () => void;
    onSave: (values: CustomerFormValues) => Promise<void>;
};

const initialSort: SortDescriptor[] = [{ field: "name", dir: "asc" }];

const customerStatusOptions = [
    { text: "Active", value: CustomerStatus.Active },
    { text: "Inactive", value: CustomerStatus.Inactive }
];

const CustomerStatusField = ({ id, value, onChange, onBlur, onFocus }: FieldRenderProps) => (
    <DropDownList
        id={id}
        label="Status"
        data={customerStatusOptions}
        textField="text"
        dataItemKey="value"
        value={customerStatusOptions.find(option => option.value === value)}
        onChange={event => onChange({ value: event.value.value })}
        onBlur={onBlur}
        onFocus={onFocus}
    />
);

const customerNameValidator = (value: string | undefined) => {
    const name = value?.trim() ?? "";
    if (!name) {
        return "Customer name is required.";
    }

    if (name.length > 200) {
        return "Customer name must be 200 characters or less.";
    }

    return "";
};

const CustomerFormWindow: React.FC<CustomerFormWindowProps> = ({ customer, errorMessage, isSaving, onClose, onSave }) => (
    <Window title={customer ? "Edit Customer" : "Create Customer"} width={500} modal onClose={onClose}>
        {errorMessage && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}
        <Form
            initialValues={{ name: customer?.name ?? "", status: customer?.status ?? CustomerStatus.Active }}
            onSubmit={(values: Record<string, unknown>) => void onSave(values as CustomerFormValues)}
            render={(formRenderProps: FormRenderProps) => (
                <FormElement>

                    <div className="mb-2">
                        <Field
                            id="customerName"
                            name="name"
                            label="Name"
                            component={FormInputField}
                            validator={customerNameValidator}
                        />
                    </div>

                    <div className="mb-2">
                        <Field
                            id="customerStatus"
                            name="status"
                            component={CustomerStatusField}
                        />
                    </div>

                    <WindowActionsBar>
                        <Button type="button" onClick={onClose} disabled={isSaving}>Cancel</Button>
                        <Button type="submit" themeColor="primary" disabled={!formRenderProps.allowSubmit || isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </WindowActionsBar>
                </FormElement>
            )}
        />
    </Window>
);

const CustomerManagement: React.FC = () => {
    const [triggerListCustomersQuery, { data, isLoading, isFetching }] = useLazyListCustomersQuery();
    const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
    const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();
    const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation();
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | undefined>();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({
        skip: 0,
        take: 20,
        sort: initialSort
    });

    const refreshCustomers = useCallback(() => {
        triggerListCustomersQuery({
            ...dataState
        });
    }, [triggerListCustomersQuery, dataState]);

    useEffect(() => {
        refreshCustomers();
    }, [refreshCustomers]);

    const pageChange = (event: GridPageChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            skip: event.page.skip,
            take: event.page.take
        }));
    };

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        setDataState({
            ...event.dataState,
            skip: event.dataState.skip,
            take: event.dataState.take
        });
    };

    const sortChange = (event: GridSortChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            sort: event.sort
        }));
    };

    const openCreateWindow = () => {
        setSelectedCustomer(undefined);
        setFormErrorMessage(null);
        setIsEditorOpen(true);
    };

    const openEditWindow = (customer: CustomerDto) => {
        setSelectedCustomer(customer);
        setFormErrorMessage(null);
        setIsEditorOpen(true);
    };

    const handleSave = async (values: CustomerFormValues): Promise<void> => {
        try {
            const name = values.name.trim();
            const status = values.status;
            if (selectedCustomer) {
                await updateCustomer({ customer_id: selectedCustomer.id, name, status }).unwrap();
            } else {
                await createCustomer({ name, status }).unwrap();
            }

            setErrorMessage(null);
            setFormErrorMessage(null);
            setIsEditorOpen(false);
            refreshCustomers();
        } catch {
            setFormErrorMessage("Unable to save customer.");
        }
    };

    const handleDelete = async (customer: CustomerDto) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete ${customer.name}?`);
        if (!isConfirmed) {
            return;
        }

        try {
            await deleteCustomer({ customer_id: customer.id }).unwrap();
            setErrorMessage(null);
            refreshCustomers();
        } catch {
            setErrorMessage("Unable to delete customer.");
        }
    };

    const customerResult = (data?.data ?? []).map(customer => ({
        ...customer,
        createdAt: new Date(customer.created_at),
        updatedAt: new Date(customer.updated_at)
    }));

    const ActionsCell = (props: GridCellProps) => {
        const customer = props.dataItem as CustomerDto;
        return (
            <td className="k-table-td">
                <div className="d-flex gap-2">
                    <Button
                        type="button"
                        svgIcon={pencilIcon}
                        fillMode="outline"
                        size="small"
                        disabled={isDeleting || isFetching}
                        onClick={() => openEditWindow(customer)}
                    >
                        Edit
                    </Button>
                    <Button
                        type="button"
                        svgIcon={trashIcon}
                        themeColor="error"
                        fillMode="outline"
                        size="small"
                        disabled={isDeleting || isFetching}
                        onClick={() => void handleDelete(customer)}
                    >
                        Delete
                    </Button>
                </div>
            </td>
        );
    };

    return (
        <div className="container-fluid py-3 d-flex flex-column h-100 min-h-0 overflow-hidden">
            <div className="d-flex flex-column gap-2 mb-3">
                <h2 className="h4 mb-0">Customer Management</h2>
                <p className="text-muted mb-0">Create, update, and delete customer tenants.</p>
            </div>

            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}

            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    scrollable="scrollable"
                    showLoader={isLoading || isFetching || isCreating || isUpdating || isDeleting}
                    dataItemKey="id"
                    data={customerResult}
                    total={data?.total ?? 0}
                    pageable={{ buttonCount: 3, pageSizes: [20, 50, 100] }}
                    sortable={true}
                    initialState={dataState}
                    skip={dataState.skip}
                    take={dataState.take}
                    sort={dataState.sort}
                    defaultSort={initialSort}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    onSortChange={sortChange}
                    style={{ width: "100%", height: "100%" }}
                >
                    <GridToolbar>
                        <Button type="button" svgIcon={plusIcon} themeColor="primary" onClick={openCreateWindow}>
                            Create Customer
                        </Button>
                        <Button type="button" svgIcon={arrowRotateCwIcon} onClick={refreshCustomers}>
                            Refresh
                        </Button>
                    </GridToolbar>
                    <GridColumn field="name" title="Name" />
                    <GridColumn
                        field="status"
                        title="Status"
                        width="140px"
                        cells={{ data: props => (
                            <td className="k-table-td">{CustomerStatus[(props.dataItem as CustomerDto).status] ?? "Unknown"}</td>
                        ) }}
                    />
                    <GridColumn field="createdAt" title="Created" filterable={false} format="{0:d/M/yy h:mm.ss a}" />
                    <GridColumn field="updatedAt" title="Updated" filterable={false} format="{0:d/M/yy h:mm.ss a}" />
                    <GridColumn title="Actions" width="220px" cells={{ data: ActionsCell }} />
                </AppGrid>
            </div>

            {isEditorOpen && (
                <CustomerFormWindow
                    customer={selectedCustomer}
                    errorMessage={formErrorMessage}
                    isSaving={isCreating || isUpdating}
                    onClose={() => setIsEditorOpen(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default CustomerManagement;
